"use strict";
const _ = require('lodash');
const shopConfigModel = require('../shopConfig');
const { getRegion } = require('../customerSubUnit');
const cartStateEnum = {
    building: 0,
    sentToExternalSystem: 1,
    readyForPurchaseInExternalSystem: 2,
};
const isCompleteSKU = sku => sku.quantityOfMainUnitPerUnit !== undefined &&
    sku.quantityOfMainUnitPerUnit !== null;
const getSortedCarts = carts => {
    const sortedCarts = _.orderBy(carts, [
        cart => cartStateEnum[cart.cartState], // Sort by cartState enum order
        'cartType', // Sort by cartType
        'createdAt',
    ], ['asc', 'asc', 'desc']);
    return sortedCarts;
};
/* Used as socket channel room name AND logical key together with OFC to find the correct cart */
const getMultiCartKey = (customerSubUnitId, supplierCustomerId, cartOrigin) => `${customerSubUnitId}_${supplierCustomerId}_${cartOrigin}`;
const getMultiCartKeyFromSessionUser = (sessionUser, cartOrigin) => {
    const customerSubUnitId = _.get(sessionUser.activeMembership, 'customerSubUnit.id');
    const supplierCustomerId = _.get(sessionUser.activeMembership, 'supplierCustomer.id');
    return getMultiCartKey(customerSubUnitId, supplierCustomerId, cartOrigin);
};
const getCartOrigin = domainName => shopConfigModel.isPro({ subdomain: domainName }) ? 'purchasing' : domainName;
const getCartKey = cart => {
    // sometimes the cart input is not a full cart but an object containing properties from a article
    if (cart.cartState &&
        cart.cartState !== 'building' &&
        cart.cartType === 'external') {
        return cart.id;
    }
    return `${cart.orderFullfillmentCenter.id}-${cart.currency}`;
};
const getCartTotal = cart => _.sum((cart.cartRows || cart.orderRows) // this is not good its because we send both order and cart to the same function - can be cleaned up
    .filter(x => !x.isPricedArticleStale &&
    x.pricedArticle &&
    x.pricedArticle.myOrderFullfillmentCenter)
    .map(row => {
    const quantity = row.quantity || row.deliveredQuantity;
    const orderSKUModifier = _.get(row, 'orderedSKU.quantityOfMainUnitPerUnit', 1);
    const salesPrice = _.get(row, 'pricedArticle.salesPrice', _.get(row, 'price', 0));
    return quantity * salesPrice * orderSKUModifier;
}));
const getCartRecycleAmount = cart => _.sum((cart.cartRows || cart.orderRows) // this is not good its because we send both order and cart to the same function - can be cleaned up
    .filter(x => !x.isPricedArticleStale &&
    x.pricedArticle &&
    x.pricedArticle.myOrderFullfillmentCenter)
    .map(row => (row.quantity || row.deliveredQuantity) *
    _.get(row, 'pricedArticle.recycleAmount', _.get(row, 'article.recycleAmount', 0))));
const getNumberOfOrderUnitEquivalentsInRows = (cartRows, orderFullfillmentCenter) => {
    const result = _.sumBy(cartRows, cartRow => {
        if (cartRow.isPricedArticleStale ||
            !cartRow.pricedArticle ||
            !cartRow.pricedArticle.myOrderFullfillmentCenter) {
            return 0;
        }
        const { pricedArticle, quantity } = cartRow;
        const orderUnitSize = pricedArticle.orderUnit === 'single'
            ? 1
            : _.get(pricedArticle, 'colliSize', 1);
        const nrOfOrderUnits = (quantity || 0) / orderUnitSize;
        if (pricedArticle.container === 'Keg') {
            const kegCountsAsNCollis = orderFullfillmentCenter.kegCountsAsNCollis || 1;
            return nrOfOrderUnits * kegCountsAsNCollis;
        }
        return nrOfOrderUnits;
    });
    return result;
};
const getMinimumOrderRequirements = (cart, shopConfig) => {
    const { orderFullfillmentCenter } = cart;
    const orderRequirements = shopConfig && shopConfig.hasShopSpecificOrderRequirements
        ? shopConfig
        : orderFullfillmentCenter;
    const region = getRegion(cart.supplierCustomer || cart.customerSubUnit);
    const supplierCustomer = cart.supplierCustomer;
    const minCollisOnOrderMinRegion = (supplierCustomer && supplierCustomer.minCollisOnOrder) ||
        (!_.isEmpty(orderRequirements.minCollisOnOrderByRegion || {}) &&
            _.min(Object.values(orderRequirements.minCollisOnOrderByRegion)));
    const minOrderValueMinRegion = (supplierCustomer && supplierCustomer.minOrderValue) ||
        (!_.isEmpty(orderRequirements.minOrderValueByRegion || {}) &&
            _.min(Object.values(orderRequirements.minOrderValueByRegion)));
    const isPossibleToPlaceOrderBelowOrderMinimum = (supplierCustomer &&
        supplierCustomer.isPossibleToPlaceOrderBelowOrderMinimum) ||
        !!orderRequirements.isPossibleToPlaceOrderBelowOrderMinimum;
    // Even if a orderRequirements has both requirements set, only one is used. minCollisOnOrder has precedence over minOrderValue.
    if (_.isFinite(minCollisOnOrderMinRegion)) {
        const minimum = (supplierCustomer && supplierCustomer.minCollisOnOrder) ||
            orderRequirements.minCollisOnOrderByRegion[region] ||
            minCollisOnOrderMinRegion;
        const current = getNumberOfOrderUnitEquivalentsInRows(cart.cartRows || cart.orderRows, // this is not good its because we send both order and cart to the same function - can be cleaned up
        orderRequirements);
        const isMinimumValueSatisfied = current - minimum >= 0;
        return {
            orderFullfillmentCenter,
            minimum,
            current,
            currency: cart.currency,
            type: 'minCollisOnOrder',
            localizedName: 'Kollikrav',
            isMinimumValueSatisfied,
            isPossibleToPlaceOrderBelowOrderMinimum,
            needsToAcceptFreightPaymentCharges: isMinimumValueSatisfied && isPossibleToPlaceOrderBelowOrderMinimum,
        };
    }
    const current = getCartTotal(cart);
    if (_.isFinite(minOrderValueMinRegion)) {
        const minimum = (supplierCustomer && supplierCustomer.minOrderValue) ||
            orderRequirements.minOrderValueByRegion[region] ||
            minOrderValueMinRegion;
        const isMinimumValueSatisfied = current - minimum >= 0;
        return {
            orderFullfillmentCenter,
            minimum,
            current,
            currency: cart.currency,
            type: 'minOrderValue',
            localizedName: 'Minimibelopp',
            isMinimumValueSatisfied: current - minimum >= 0,
            isPossibleToPlaceOrderBelowOrderMinimum,
            needsToAcceptFreightPaymentCharges: isMinimumValueSatisfied && isPossibleToPlaceOrderBelowOrderMinimum,
        };
    }
    // if no requirement is set, we will not show any requirement
    return {
        orderFullfillmentCenter,
        minimum: 0,
        current,
        currency: cart.currency,
        type: 'minOrderValue',
        localizedName: 'Minimibelopp',
        isMinimumValueSatisfied: true,
        isPossibleToPlaceOrderBelowOrderMinimum: false,
        needsToAcceptFreightPaymentCharges: false,
    };
};
module.exports = {
    isCompleteSKU,
    getSortedCarts,
    getCartTotal,
    getCartRecycleAmount,
    getMinimumOrderRequirements,
    getCartKey,
    getCartOrigin,
    getMultiCartKey,
    getMultiCartKeyFromSessionUser,
};
